<template>
  <v-container fluid>
    <v-row
      v-if="userHasEditPermissions"
      :justify="$vuetify.breakpoint.mdAndDown ? 'center' : 'end'"
      no-gutters
      class="pa-0"
    >
      <v-switch v-model="hideEditControls" label="View as recipient" hide-details />
    </v-row>
    <v-row v-if="showEditControls || userHasViewPermissions" justify="center">
      <v-col cols="12" md="6">
        <v-alert dismissible :value="!isPublished" type="error" border="left">
          Your video is not publicly accessible.
        </v-alert>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col :cols="showEditControls ? 10 : 12" :md="showEditControls ? 5 : 6">
        <video-player :source="videoUrl" :thumbnail="thumbnail" :status="status" :short-id="shortId" />
        <user-details :user="user">
          <template v-if="showEditControls" #user-actions>
            <v-list-item-action>
              <base-icon-button
                button-outlined
                :button-icon="displayContactDetails ? ['fad', 'eye'] : ['fad', 'eye-slash']"
                :tool-tip="displayContactDetails ? 'Hide Contact Details' : 'Show Contact Details'"
                @click="toggleDisplayContactDetails()"
              ></base-icon-button>
            </v-list-item-action>
          </template>
        </user-details>
        <v-divider></v-divider>
        <video-details />
        <v-divider></v-divider>
        <links v-if="links && links.length" :user-has-edit-permissions="false" :links="links" />
      </v-col>
      <v-col v-if="showEditControls" cols="2" :md="1">
        <side-panel />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import VideoPlayer from '../../components/dashboard/VideoPlayer';
import VideoDetails from '../../components/landing/VideoDetails';
import SidePanel from '../../components/landing/SidePanel';
import Links from '../../components/landing/Links';
import UserDetails from '../../components/landing/UserDetails';

export default {
  name: 'Id',
  components: { VideoPlayer, VideoDetails, SidePanel, Links, UserDetails },
  layout: 'landing',
  auth: false,
  data() {
    return {
      hideEditControls: false,
    };
  },
  async fetch({ store, route, error, req, redirect }) {
    try {
      const video = await store.dispatch('video/fetchPublished', route.params.id);
      await store.commit('video/set', video);

      const { whiteLabelDomain } = video.user.client.metadata;
      await store.dispatch('application/setWhitelabelDomain', whiteLabelDomain);

      if (whiteLabelDomain && !store.state.auth.loggedIn && req && !req.hostname.includes(whiteLabelDomain)) {
        return redirect('https://producer.' + whiteLabelDomain + route.path);
      }

      if (!video)
        return error({
          statusCode: 410,
          message: `Unable to retrieve video.`,
        });
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return error({
          statusCode: 410,
          message: `This video is currently unavailable as it has been removed or deleted.`,
        });
      }
      return error({
        statusCode: 500,
        message: 'A problem occurred trying to load initial video data.',
        errorMessage: err.message,
      });
    }
  },
  head() {
    const title = this.videoName || `Video message from ${this.user.name}`;
    const description = this.videoDescription || `Video message from ${this.user.name}`;
    const faviconUrl = this.video.user.client.metadata.favicon;

    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'og:title', property: 'og:title', content: this.videoName },
        { hid: 'og:description', property: 'og:description', content: description },
        { hid: 'og:image', property: 'og:image', content: this.thumbnailUrl },
      ],
      ...(faviconUrl && {
        link: [
          {
            rel: 'icon',
            hid: 'icon',
            type: 'image/x-icon',
            href: `${this.$config.visionApiEndpoint}${faviconUrl}`,
          },
        ],
      }),
    };
  },
  computed: {
    ...mapState('video', {
      isPublished: state => state.current.isPublished,
    }),
    ...mapFields('video', {
      video: 'current',
      displayContactDetails: 'current.displayContactDetails',
      videoName: 'current.metadata.name',
      videoDescription: 'current.metadata.description',
      subtitledUrl: 'current.subtitledUrl',
      url: 'current.url',
      thumbnail: 'current.thumbnail',
      thumbnailOverlaidUrl: 'current.thumbnailOverlaidUrl',
      user: 'current.user',
      links: 'current.links',
      previewInProgress: 'previewInProgress',
    }),
    videoUrl() {
      if (this.status !== 'COMPLETED') {
        return this.video.url;
      }
      return this.video.subtitledUrl || this.video.url;
    },
    shortId() {
      return this.video.shortId;
    },
    userHasEditPermissions() {
      if (!this.$store.state.auth.loggedIn) return false;
      const user = this.$auth.user;
      const userOwnsVideo = user._id === this.video.userId;
      const adminUser = user.accessLevel === 'admin' && user.client._id === this.video.clientId;
      return userOwnsVideo || adminUser;
    },
    userHasViewPermissions() {
      if (!this.$store.state.auth.loggedIn) return false;
      const user = this.$auth.user;
      return user.accessLevel === 'standardPlus' && user.client._id === this.video.clientId;
    },
    showEditControls() {
      return this.userHasEditPermissions && !this.hideEditControls;
    },
    finalOrSubtitledVideoInProgress() {
      return this.video.status !== 'COMPLETED' && !this.previewInProgress;
    },
    status() {
      return this.finalOrSubtitledVideoInProgress ? 'IN_PROGRESS' : 'COMPLETED';
    },
    thumbnailUrl() {
      let url = this.thumbnail;

      if (this.thumbnailOverlaidUrl && !this.user.hidePlayIconProducer) {
        url = this.thumbnailOverlaidUrl;
      }

      return url;
    },
  },
  mounted() {
    if (this.finalOrSubtitledVideoInProgress) {
      this.$store.dispatch('video/watchJobStatus', this.video._id);
    }

    this.$store.dispatch('video/logViewEvent', { id: this.video.shortId });
    this.$store.dispatch('video/dispatchEvent', {
      viewerUserId: this.$auth?.user?._id,
      contextId: this.video.shortId,
      contextKey: 'PRODUCER',
      eventType: 'VIEW',
    });
  },
  methods: {
    async toggleDisplayContactDetails() {
      this.displayContactDetails = !this.displayContactDetails;

      try {
        await this.$store.dispatch('video/update');
        this.$notify({
          group: 'app',
          type: 'success',
          title: 'Success!',
          text: `You contact details preference has been set.`,
        });
      } catch (err) {
        this.displayContactDetails = !this.displayContactDetails;

        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Error!',
          text: `A problem occurred while trying to set contact details preference: ${err.message}`,
          duration: 5000,
        });
      }
    },
  },
};
</script>
